import type { ConversationItemProps } from '@/@studio-ai/props';
import { highlightText } from '@/@studio-ai/utils/highlight-utils';
import { formatRelativeDate } from '@/lib/utils/formatting';
import { Chip, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ConversationItemMenu } from './conversation-item-menu';

export function ConversationItem({
    conversation,
    isActive = false,
    onSelect,
    onDelete,
    onRename,
    onShare,
    onDuplicate,
    folders,
    onMoveToFolder,
    isLoading = false,
    className = '',
    searchTerm = '',
}: ConversationItemProps) {
    const title = conversation.title || `Chat ${conversation.conversationId.slice(-8)}`;
    const matchedSnippet = conversation.matchedSnippet;
    const messageCount = conversation.messageCount || 0;

    return (
        <div
            className={`group/row flex items-center gap-3 px-4 py-2.5 transition-colors ${
                isActive
                    ? 'bg-default-100'
                    : isLoading
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:bg-default-50'
            } ${className}`}
        >
            <Icon
                icon="lucide:grip-vertical"
                width={12}
                className="shrink-0 cursor-grab text-default-300"
            />
            <div
                className={`flex min-w-0 flex-1 items-center gap-2 overflow-hidden ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => !isLoading && onSelect()}
            >
                {conversation.masqueradeOrgName ? (
                    <Tooltip content={`Masquerading as ${conversation.masqueradeOrgName}`}>
                        <Icon
                            icon="lucide:venetian-mask"
                            width={14}
                            className="shrink-0 text-primary"
                        />
                    </Tooltip>
                ) : (
                    <Icon
                        icon="lucide:message-square"
                        width={14}
                        className="shrink-0 text-default-400"
                    />
                )}

                <div className="overflow-hidden">
                    <div className="truncate text-small text-foreground">{title}</div>
                    <div className="truncate text-small text-foreground-500">
                        {highlightText(matchedSnippet ?? '', searchTerm)}
                    </div>
                </div>
            </div>
            <Chip size="sm" variant="flat" className="h-5 min-w-0 px-1.5 text-mini">
                {messageCount} message{messageCount === 1 ? '' : 's'}
            </Chip>
            <span className="shrink-0 text-mini text-default-400">
                {formatRelativeDate(conversation.lastActivityAt)}
            </span>
            <div className="w-8 shrink-0">
                <div className="flex justify-end transition-opacity">
                    <ConversationItemMenu
                        conversation={conversation}
                        folders={folders ?? []}
                        onOpen={onSelect}
                        onRename={onRename}
                        onDelete={onDelete}
                        onShare={onShare ?? (() => {})}
                        onDuplicate={onDuplicate ?? (() => {})}
                        onMoveToFolder={onMoveToFolder ?? (() => {})}
                    />
                </div>
            </div>
        </div>
    );
}
